import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { IssuedApiCredentialMaterial } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { fetchAllAdminApplications } from '@/api/fetchAll';
import { payApiClient } from '@/api/client';
import { FilterableSelect } from '@/components/form/FilterableSelect';
import { TagsField } from '@/features/operations/filters/TagsField';
import { useApiResource } from '@/hooks/useApiResource';

interface CredentialCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fires with the once-only `secret`/`signingSecret` pair (D-N) — the caller must show the reveal panel and never store it. */
  onCreated: (credential: IssuedApiCredentialMaterial) => void;
}

/**
 * `POST /v1/admin/api-credentials`. The `secret`/`signingSecret` pair this
 * call generates never touches this dialog's own state beyond the instant
 * it hands them to `onCreated` — see `CredentialSecretRevealPanel` for how
 * the caller displays and discards them.
 */
export function CredentialCreateDialog({ open, onClose, onCreated }: CredentialCreateDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [applicationCode, setApplicationCode] = useState('');
  const [allowAllTenants, setAllowAllTenants] = useState(false);
  const [allowedTenantCodes, setAllowedTenantCodes] = useState<string[]>([]);
  const [ipAllowlist, setIpAllowlist] = useState<string[]>([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // MUI's `Dialog` mounts its children even while `open` is `false`, so the
  // fetcher guards on `open` itself — the applications catalog only loads
  // once the dialog is actually shown.
  const applications = useApiResource(
    () => (open ? fetchAllAdminApplications() : Promise.resolve([])),
    `credential-create-applications-${open}`,
  );

  function resetAndClose() {
    setApplicationCode('');
    setAllowAllTenants(false);
    setAllowedTenantCodes([]);
    setIpAllowlist([]);
    setAccessDenied(false);
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const credential = await payApiClient.createAdminApiCredential({
        applicationCode: applicationCode.trim(),
        allowAllTenants,
        allowedTenantCodes: allowAllTenants ? [] : allowedTenantCodes,
        ipAllowlist,
      });
      setApplicationCode('');
      setAllowAllTenants(false);
      setAllowedTenantCodes([]);
      setIpAllowlist([]);
      onCreated(credential);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = applicationCode.trim().length > 0 && !submitting;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.credentials.create.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <FilterableSelect
              autoFocus
              required
              label={translate('pay.credentials.create.fieldApplication')}
              helperText={translate('pay.credentials.create.fieldApplicationHelp')}
              options={(applications.data ?? []).map((application) => ({
                value: application.code,
                label: application.name,
                secondary: application.code,
              }))}
              loading={applications.loading}
              value={applicationCode}
              onChange={(code) => setApplicationCode(code)}
            />
            <FormControlLabel
              control={<Switch checked={allowAllTenants} onChange={(event) => setAllowAllTenants(event.target.checked)} />}
              label={translate('pay.credentials.create.fieldAllowAllTenants')}
            />
            {!allowAllTenants && (
              <TagsField
                label={translate('pay.credentials.create.fieldAllowedTenantCodes')}
                helperText={translate('pay.credentials.create.fieldAllowedTenantCodesHelp')}
                values={allowedTenantCodes}
                onChange={setAllowedTenantCodes}
              />
            )}
            <TagsField
              label={translate('pay.credentials.create.fieldIpAllowlist')}
              helperText={translate('pay.credentials.create.fieldIpAllowlistHelp')}
              values={ipAllowlist}
              onChange={setIpAllowlist}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>{translate('pay.credentials.create.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {translate('pay.credentials.create.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
